/**
 * Follow-camera fit math: screen-space margins, UI-aware insets, zoom-out guardrail.
 */

import {
  BAR_BOTTOM_HEIGHT_PERCENT,
  BAR_TOP_HEIGHT_PERCENT,
  CAMERA_TOP_GAP_REM,
  FAB_SIZE_REM_MD,
  PANEL_TOP_INSET_REM,
} from '~/overlay/overlaySpacing';
import {
  SIDE_PANEL_VIEWPORT_LG_MIN,
  SIDE_PANEL_WIDTH_FRACTION,
} from '~/overlay/sidePanelDimensions';

import { BoundingBox } from './cameraPoi';

/** Target on-screen margin band around content (fraction of available viewport). */
export const FOLLOW_MARGIN_MIN = 0.1;
export const FOLLOW_MARGIN_MAX = 0.16;
export const FOLLOW_MARGIN_BASE = 0.12;

/** Medium zoom-out guardrail: avoid showing more than ~70% of board width unless POI requires it. */
export const MAX_BOARD_VISIBLE_WIDTH_FRACTION = 0.7;

export const BOARD_AR = 3072 / 2400;

export interface ViewportInsets {
  top: number;
  bottom: number;
  left: number;
  right: number;
  centerX: number;
  centerY: number;
}

export interface PanelStateForInsets {
  history: { isVisible: boolean; isExpanded: boolean };
  chat: { isVisible: boolean; isExpanded: boolean };
}

function remToViewportHeightFraction(rem: number): number {
  if (typeof window === 'undefined') {
    return (rem * 16) / 900;
  }
  const rootPx =
    typeof document !== 'undefined'
      ? parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
      : 16;
  return (rem * rootPx) / window.innerHeight;
}

/**
 * Derive viewport insets from overlay chrome (bars, panels, camera control cluster).
 */
export function computeDynamicInsets(panelState: PanelStateForInsets): ViewportInsets {
  const isLargeScreen =
    typeof window !== 'undefined' && window.innerWidth >= SIDE_PANEL_VIEWPORT_LG_MIN;

  const historyWidth = panelState.history.isVisible
    ? panelState.history.isExpanded
      ? SIDE_PANEL_WIDTH_FRACTION.expanded[isLargeScreen ? 'lg' : 'sm']
      : SIDE_PANEL_WIDTH_FRACTION.collapsed[isLargeScreen ? 'lg' : 'sm']
    : 0;

  const chatWidth = panelState.chat.isVisible
    ? panelState.chat.isExpanded
      ? SIDE_PANEL_WIDTH_FRACTION.expanded[isLargeScreen ? 'lg' : 'sm']
      : SIDE_PANEL_WIDTH_FRACTION.collapsed[isLargeScreen ? 'lg' : 'sm']
    : 0;

  const leftInset = historyWidth;
  const rightInset = chatWidth;

  const topBar = BAR_TOP_HEIGHT_PERCENT / 100;
  const cameraClusterRem = panelState.chat.isVisible
    ? PANEL_TOP_INSET_REM
    : CAMERA_TOP_GAP_REM + FAB_SIZE_REM_MD;
  const topInset = Math.min(0.45, topBar + remToViewportHeightFraction(cameraClusterRem));
  const bottomInset = BAR_BOTTOM_HEIGHT_PERCENT / 100;

  const centerX = leftInset + (1 - leftInset - rightInset) / 2;
  const centerY = topInset + (1 - topInset - bottomInset) / 2;

  return {
    top: topInset,
    bottom: bottomInset,
    left: leftInset,
    right: rightInset,
    centerX,
    centerY,
  };
}

/**
 * Context-first margin: compact clusters get more breathing room (up to 16%).
 */
export function adaptiveMarginFraction(bbox: BoundingBox): number {
  const span = Math.max(bbox.width, bbox.height);
  if (span < 0.08) return FOLLOW_MARGIN_MAX;
  if (span < 0.15) return 0.14;
  return FOLLOW_MARGIN_BASE;
}

/**
 * Raw zoom-to-fit with screen-space margin (replaces static FIT_PADDING_FACTOR approach).
 */
export function computeRawFitScale(
  bbox: BoundingBox,
  insets: ViewportInsets,
  marginFraction: number,
): number {
  const screenAR = typeof window !== 'undefined' ? window.innerWidth / window.innerHeight : 16 / 9;

  const availableWidth = 1 - insets.left - insets.right;
  const availableHeight = 1 - insets.top - insets.bottom;

  const contentWidthBudget = availableWidth * (1 - 2 * marginFraction);
  const contentHeightBudget = availableHeight * (1 - 2 * marginFraction);

  const scaleX = contentWidthBudget / bbox.width;
  const scaleY = (contentHeightBudget * BOARD_AR) / (screenAR * bbox.height);

  return Math.min(scaleX, scaleY);
}

/**
 * Minimum scale (most zoomed-in allowed by guardrail) before POI relaxation.
 * Lower scale = more zoomed out; guardrail prevents scale dropping below this when no hard POI.
 */
export function computeGuardrailMinScale(insets: ViewportInsets): number {
  const availableWidth = 1 - insets.left - insets.right;
  return availableWidth / MAX_BOARD_VISIBLE_WIDTH_FRACTION;
}

export function applyZoomOutGuardrail(
  rawFitScale: number,
  insets: ViewportInsets,
  relaxGuardrail: boolean,
): number {
  const guardrailMin = computeGuardrailMinScale(insets);
  if (relaxGuardrail) {
    return rawFitScale;
  }
  return Math.max(rawFitScale, guardrailMin);
}

export function computeFocusPoint(
  bbox: BoundingBox,
  insets: ViewportInsets,
  scale: number,
): { x: number; y: number } {
  const screenAR = typeof window !== 'undefined' ? window.innerWidth / window.innerHeight : 16 / 9;

  const contentCenterX = bbox.centerX;
  const contentCenterY = bbox.centerY;

  const offsetX = (insets.centerX - 0.5) / scale;
  const offsetY = ((insets.centerY - 0.5) * BOARD_AR) / (screenAR * scale);

  return {
    x: contentCenterX - offsetX,
    y: contentCenterY - offsetY,
  };
}
